import { Injectable } from '@angular/core';

export interface IAudio {
  audio: HTMLAudioElement;
  waitForEnd: () => Promise<void>;
}

@Injectable({
  providedIn: 'root',
})
export class AudioService {
  private _loopIdCounter = 0;
  private _activeLoops: Map<number, { stop: () => void }> = new Map();

  public play(url: string, options?: Partial<{ volume: number }>): IAudio {
    const audio = this.getAudio(url, options);
    let ended = false;
    audio.addEventListener('ended', () => (ended = true));
    audio.play();
    return {
      audio,
      waitForEnd: async () => {
        if (ended) return; // already ended

        return new Promise<void>((resolve) => {
          audio.addEventListener(
            'ended',
            () => {
              ended = true;
              resolve();
            },
            { once: true },
          );
        });
      },
    };
  }

  /**
   * Loop audio with optional delay and options. Returns a loopId for stopping later.
   * @param url
   * @param options
   * @param delayMs Delay in milliseconds before each play (including initial)
   * @returns loopId
   */
  public loop(url: string, options?: Partial<{ volume: number }>, delayMs: number = 0): number {
    const loopId = ++this._loopIdCounter;
    let stopped = false;
    const playLoop = async () => {
      while (!stopped) {
        if (delayMs > 0) {
          await new Promise((res) => setTimeout(res, delayMs));
        }
        if (stopped) break;
        const audio = this.getAudio(url, options);
        await new Promise<void>((resolve) => {
          audio.addEventListener('ended', () => resolve(), { once: true });
          audio.play();
        });
      }
    };
    playLoop();
    this._activeLoops.set(loopId, {
      stop: () => {
        stopped = true;
      },
    });
    return loopId;
  }

  /**
   * Stop a looping audio by its loopId.
   */
  public stopLoop(loopId: number) {
    const entry = this._activeLoops.get(loopId);
    if (entry) {
      entry.stop();
      this._activeLoops.delete(loopId);
    }
  }

  /**
   * Stop all currently looping audios.
   */
  public stopAllLoops() {
    for (const [_, entry] of this._activeLoops.entries()) {
      entry.stop();
    }
    this._activeLoops.clear();
  }

  private getAudio(url: string, options?: Partial<{ volume: number }>) {
    const audio = new Audio(url);
    if (options?.volume) audio.volume = options.volume;
    return audio;
  }
}
